import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Card, SectionHead, Pill, Btn, Field, Input, Select, Table } from "./ui";
import { HC } from "./theme";
import { toast } from "sonner";
import { CalendarRange, Sparkles, Send, Edit3, Ban, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const iso = (d) => d.toISOString().slice(0, 10);

export default function ShiftPlanner() {
  const { locationId } = useOutletContext();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ location_id: locationId || "", period_type: "weekly", period_start: iso(new Date()) });
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/hr/schedule-plans", { params: { location_id: locationId || undefined } })
      .then(setPlans).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/locations").then(setLocations).catch(() => {});
    api.get("/hr/employees", { params: { location_id: locationId || undefined } })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || r?.rows || [])))
      .catch(() => {});
    api.get("/hr/shift-types").then(setShiftTypes).catch(() => {});
    /* eslint-disable-next-line */
  }, [locationId]);

  const generate = async () => {
    if (!genForm.location_id || !genForm.period_start) { toast.error("Pick a branch and start date"); return; }
    try {
      const plan = await api.post("/hr/schedule-plans/generate", genForm);
      toast.success(`${plan.period_type} draft generated — ${plan.slots.length} slot${plan.slots.length === 1 ? "" : "s"} (${plan.unfilled_slots} unfilled)`);
      setGenOpen(false); load();
      const full = await api.get(`/hr/schedule-plans/${plan.id}`);
      setDetail(full);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <SectionHead title="Intelligent Shift Planner"
          sub="Pulls active staff, excludes anyone on approved leave, and favours reliable attendance per day of week. Every generated slot is a system suggestion — review and submit for approval before it goes live."
          right={<Btn onClick={() => setGenOpen(true)} data-testid="gen-open-btn"><Sparkles size={15} /> Generate Schedule</Btn>} />
        <Table
          testid="plans-table"
          columns={[
            { key: "period_start", label: "Period", render: (r) => `${r.period_start} → ${r.period_end}` },
            { key: "location_name", label: "Branch" },
            { key: "period_type", label: "Type", render: (r) => <Pill status={r.period_type} text={r.period_type} /> },
            { key: "status", label: "Status", render: (r) => <Pill status={r.status} /> },
            { key: "created_by_name", label: "Manager" },
            { key: "_a", label: "", align: "right", render: (r) => <Btn variant="ghost" data-testid={`plan-open-${r.id}`} onClick={async () => { const full = await api.get(`/hr/schedule-plans/${r.id}`); setDetail(full); }}>Open</Btn> },
          ]}
          rows={plans}
          empty={loading ? "Loading…" : "No schedule drafts yet. Generate one to get started."}
        />
      </Card>

      <Dialog open={genOpen} onOpenChange={(o) => !o && setGenOpen(false)}>
        <DialogContent className="max-w-md" data-testid="gen-dialog">
          <DialogHeader>
            <DialogTitle>Generate a Suggested Schedule</DialogTitle>
            <DialogDescription>Excludes approved leave and factors last-60-day attendance patterns per weekday.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Branch">
              <Select value={genForm.location_id} onChange={(e) => setGenForm((f) => ({ ...f, location_id: e.target.value }))} data-testid="gen-branch">
                <option value="">Select…</option>
                {locations.filter((l) => l.status === "active").map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </Field>
            <Field label="Period">
              <Select value={genForm.period_type} onChange={(e) => setGenForm((f) => ({ ...f, period_type: e.target.value }))} data-testid="gen-period">
                <option value="weekly">Weekly (7 days)</option>
                <option value="monthly">Monthly (calendar month)</option>
              </Select>
            </Field>
            <Field label="Start date">
              <Input type="date" value={genForm.period_start} onChange={(e) => setGenForm((f) => ({ ...f, period_start: e.target.value }))} data-testid="gen-start" />
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="ghost" onClick={() => setGenOpen(false)}>Cancel</Btn>
            <Btn onClick={generate} data-testid="gen-run"><Sparkles size={15} /> Generate</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && <PlanDetail plan={detail} onClose={() => setDetail(null)} onChanged={(p) => { load(); if (p) setDetail(p); }} employees={employees} shiftTypes={shiftTypes} />}
    </div>
  );
}


function PlanDetail({ plan, onClose, onChanged, employees, shiftTypes }) {
  const [slots, setSlots] = useState(plan.slots || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSlots(plan.slots || []); }, [plan]);
  const editable = plan.status === "draft" || plan.status === "rejected";

  const byDay = useMemo(() => {
    const g = {};
    slots.forEach((s) => { (g[s.date] = g[s.date] || []).push(s); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const setSlotEmployee = (sid, empId) =>
    setSlots((rows) => rows.map((s) => s.id === sid ? { ...s, employee_id: empId || null, employee_name: employees.find((e) => e.id === empId)?.name || null, unfilled: !empId, reason: "Manually edited", generated: s.generated } : s));

  const removeSlot = (sid) => setSlots((rows) => rows.filter((s) => s.id !== sid));

  const addSlot = (date, shiftTypeId) => {
    setSlots((rows) => [...rows, {
      id: (crypto.randomUUID ? crypto.randomUUID() : `new-${Date.now()}-${Math.random()}`),
      date, shift_type_id: shiftTypeId,
      shift_type_name: shiftTypes.find((t) => t.id === shiftTypeId)?.name,
      employee_id: null, employee_name: null,
      generated: false, unfilled: true, reason: "Manually added",
    }]);
  };

  const persistSlots = async () => {
    setSaving(true);
    try {
      const upd = await api.put(`/hr/schedule-plans/${plan.id}`, { slots });
      toast.success("Draft saved"); onChanged(upd);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitForApproval = async () => {
    setSaving(true);
    try {
      await api.put(`/hr/schedule-plans/${plan.id}`, { slots });
      const submitted = await api.post(`/hr/schedule-plans/${plan.id}/submit`, {});
      toast.success("Submitted for supervisor + location-manager approval");
      onChanged(submitted); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this schedule plan?")) return;
    try { await api.post(`/hr/schedule-plans/${plan.id}/cancel`, {}); toast.success("Cancelled"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const holes = slots.filter((s) => !s.employee_id).length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="plan-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>{plan.location_name} · {plan.period_start} → {plan.period_end}</span>
            <Pill status={plan.status} />
            {plan.status === "draft" && <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full font-bold" style={{ background: HC.goldSoft, color: HC.burgundy }}>System Suggestion</span>}
          </DialogTitle>
          <DialogDescription>{plan.generation_notes}</DialogDescription>
        </DialogHeader>

        {plan.rejection_reason && (
          <div className="rounded-xl p-3 mb-2" style={{ background: HC.redTint, color: HC.text }}>
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: HC.red }}><AlertTriangle size={15} /> Rejected — {plan.rejection_reason}</div>
            <div className="text-xs mt-1" style={{ color: HC.soft }}>Edit any slot to reset back to draft, then submit again.</div>
          </div>
        )}
        {holes > 0 && plan.status === "draft" && (
          <div className="rounded-xl p-3 mb-2 text-xs" style={{ background: HC.amberTint, color: HC.amber }} data-testid="unfilled-warning">
            <b>{holes} unfilled slot{holes === 1 ? "" : "s"}</b> — assign someone or remove the slot before you can submit.
          </div>
        )}

        <div className="space-y-3">
          {byDay.map(([day, daySlots]) => (
            <div key={day} className="rounded-2xl border p-3" style={{ background: "#fff", borderColor: HC.borderSoft }}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-sm font-semibold" style={{ color: HC.text }}>{day} · {daySlots[0]?.day_of_week}</div>
                {editable && (
                  <div className="flex gap-1 flex-wrap">
                    {shiftTypes.map((t) => (
                      <button key={t.id} onClick={() => addSlot(day, t.id)} className="text-[10px] px-2 py-1 rounded-full border" style={{ borderColor: HC.borderSoft, color: HC.soft }} data-testid={`add-slot-${day}-${t.code || t.id}`}>
                        + {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {daySlots.map((s) => (
                  <div key={s.id} className="grid grid-cols-[130px_180px_1fr_28px] gap-2 items-center text-sm">
                    <Pill status={s.employee_id ? "approved" : "absent"} text={s.shift_type_name || "Shift"} />
                    <div style={{ color: HC.soft }}>{s.position_title || "Any role"}</div>
                    {editable ? (
                      <Select value={s.employee_id || ""} onChange={(e) => setSlotEmployee(s.id, e.target.value)} data-testid={`slot-${s.id}`}>
                        <option value="">— Unassigned —</option>
                        {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                      </Select>
                    ) : (
                      <div style={{ color: HC.text }}>{s.employee_name || <i style={{ color: HC.red }}>Unassigned</i>}</div>
                    )}
                    {editable ? (
                      <button onClick={() => removeSlot(s.id)} className="p-1 rounded-full hover:bg-[#F3ECE2]" data-testid={`rm-slot-${s.id}`}><X size={14} style={{ color: HC.red }} /></button>
                    ) : <div />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {plan.approval_request && (
          <div className="rounded-xl border p-3 mt-3" style={{ background: HC.panelSoft, borderColor: HC.borderSoft }} data-testid="approval-trail">
            <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: HC.soft }}>Approval Chain</div>
            <div className="text-xs">{(plan.approval_request.chain || []).join(" → ")} · Status: <b>{plan.approval_request.status}</b></div>
            {(plan.approval_request.approvals || []).map((a, i) => (
              <div key={i} className="text-xs mt-1" style={{ color: HC.text }}>· <b>{a.symbol}</b> {a.action} by {a.actor_name}{a.note ? ` — ${a.note}` : ""}{a.reason ? ` — ${a.reason}` : ""}</div>
            ))}
          </div>
        )}

        <DialogFooter>
          {(plan.status === "draft" || plan.status === "pending_approval" || plan.status === "rejected") && (
            <Btn variant="ghost" onClick={cancel} data-testid="plan-cancel"><Ban size={14} /> Cancel Plan</Btn>
          )}
          <Btn variant="ghost" onClick={onClose}>Close</Btn>
          {editable && <>
            <Btn variant="outline" onClick={persistSlots} disabled={saving} data-testid="plan-save"><Edit3 size={14} /> Save Draft</Btn>
            <Btn onClick={submitForApproval} disabled={saving || holes > 0} data-testid="plan-submit"><Send size={14} /> Submit for Approval</Btn>
          </>}
          {plan.status === "approved" && <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: HC.green }}><CheckCircle2 size={14} /> Live — persisted to Shift Scheduling</div>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
